import React, { useCallback } from 'react';
import type { Question, ResponsePayload } from '../../types';
import { useFeedbackPopup } from 'hooks/useFeedbackPopup';
import { useEventTracking } from 'hooks/useEventTracking';
import { Icon } from '../icons/Icon';

interface QuestionFeedbackButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  question: Question;
  response?: ResponsePayload;
  isSubmitting?: boolean;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

export const QuestionFeedbackButton = React.forwardRef<HTMLButtonElement, QuestionFeedbackButtonProps>(
({
  question,
  response,
  isSubmitting = false,
  className,
  onClick,
  type,
  ...buttonProps
}, ref) => {
  const {
    FeedbackPopup,
    isSubmittingFeedback,
    openFeedbackPopup,
  } = useFeedbackPopup();
  const { track } = useEventTracking();

  const questionId = question?.id ?? null;

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (onClick) {
        onClick(event);
      }

      if (event.defaultPrevented) {
        return;
      }

      track('button_click', {
        action: 'support_cta_click',
        control_id: 'question_feedback_button',
        support_target: 'bug_report',
        support_surface: 'question_flow',
      });
      openFeedbackPopup({ question, response: response ?? null });
    },
    [onClick, openFeedbackPopup, question, response, track],
  );

  return (
    <>
      <button
        ref={ref}
        className={`z-[400] w-12 h-12 rounded-full bg-white/40 backdrop-blur-sm backdrop-brightness-[110%] [-webkit-backdrop-filter:blur(4px)_brightness(100%)] flex items-center justify-center hover:bg-white transition-colors shadow-elevation-sm border border-solid border-white/10 ${className ?? ''}`}
        type={type ?? 'button'}
        aria-label="Submit feedback"
        onClick={handleClick}
        disabled={isSubmitting || isSubmittingFeedback || !questionId}
        data-tour-id="question-flow-control-feedback"
        {...buttonProps}
      >
        <Icon name="help" className="min-w-7 min-h-7" />
      </button>

      {FeedbackPopup}
    </>
  );
});

QuestionFeedbackButton.displayName = 'QuestionFeedbackButton';
